/**
 * API Service Layer for Froth Desk Self Service Portal
 * Uses Axios for HTTP requests with proper error handling
 *
 * Base URL: process.env.REACT_APP_BASE_URL (default: http://127.0.0.1:8000/)
 *
 * Available Endpoints:
 * - Authentication: login, logout, validateSession
 * - Tickets: createTicket, getTicket, getTicketById, getAllTickets, updateTicket
 * - Contact: createLead
 * - Health: healthCheck, testSimple
 */

import axios from "axios";

// Configuration
const API_CONFIG = {
  // In development, use empty string to leverage the proxy configured in package.json
  // In production, use the full API URL from environment variable
baseURL: process.env.REACT_APP_BASE_URL,  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);

/**
 * Helper: Get JWT token from localStorage
 * @returns {string|null} JWT token or null
 */
const getJWTToken = () => localStorage.getItem("ssToken") || localStorage.getItem("token");

/**
 * Helper: Get user credentials from localStorage
 * @returns {object|null} User data with username and encryptedPassword
 */
const getUserCredentials = () => {
  const storedUser = localStorage.getItem("ssUser");
  if (!storedUser) return null;
  try {
    return JSON.parse(storedUser);
  } catch (error) {
    console.error("Error parsing stored user:", error);
    return null;
  }
};

const cleanAssignedTo = (assignedTo) => {
  if (!assignedTo || assignedTo === "-") return "-";

  // Remove @ and everything after it
  const cleanedValue = assignedTo.split('@')[0];

  // Check if it's a system user or empty
  const systemUsers = ['root', 'localhost', 'admin', 'system', ''];
  if (systemUsers.includes(cleanedValue.toLowerCase().trim())) {
    return "-";
  }

  return cleanedValue || "-";
};

// Request interceptor to add Bearer token
apiClient.interceptors.request.use((config) => {
  // Add JWT token via Authorization header for all requests
  const token = getJWTToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't intercept errors that are already handled by the API functions
    if (error._isCustomError) {
      return Promise.reject(error);
    }

    // Handle different error types
    let errorMessage = "Something went wrong";
    let validationErrors = [];
    let requestId = null;

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      // Extract request_id from response
      requestId = data?.request_id || error.headers?.['x-request-id'];

      // Handle validation errors (422) - array format
      if (status === 422 && Array.isArray(data?.detail)) {
        validationErrors = data.detail.map(err => ({
          field: err.field,
          message: err.message
        }));

        // Use first validation error as main message
        errorMessage = validationErrors.length > 0
          ? validationErrors[0].message
          : "Validation failed";
      }
      // Check if the response has our custom error format
      else if (data?.detail?.status === "false" || data?.detail?.status === false) {
        // Return the original error data for further processing
        return Promise.reject({
          ...error,
          _hasCustomError: true,
          _customError: data.detail.message || data.detail.error_type,
          _requestId: requestId,
        });
      }
      // Handle standard error responses with detail field
      else if (data?.detail) {
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : (data.detail.message || JSON.stringify(data.detail));
      }

      // Handle specific status codes
      switch (status) {
        case 401:
          errorMessage = "Authentication failed. Please check your credentials and try again.";
          break;
        case 403:
          errorMessage = "Access denied. You do not have permission to perform this action.";
          break;
        case 404:
          errorMessage = "Resource not found.";
          break;
        case 422:
          // Already handled above (validation errors)
          break;
        case 429:
          errorMessage = "Too many requests. Please wait before trying again.";
          break;
        case 500:
          errorMessage = "Server error. Please try again later.";
          break;
        case 502:
          errorMessage = "Service temporarily unavailable. Please try again later.";
          break;
        case 504:
          errorMessage = "Request timeout. Please try again.";
          break;
        default:
          if (!errorMessage || errorMessage === "Something went wrong") {
            errorMessage = data?.message || data?.error || `HTTP ${status}: Request failed`;
          }
      }
    } else if (error.request) {
      // Request made but no response received
      errorMessage = "Network error. Please check your connection and try again.";
    } else {
      // Error in request configuration
      errorMessage = error.message || "Unknown error occurred";
    }

    console.error("API Error:", errorMessage, error);

    // Return consistent error format
    return Promise.reject({
      success: false,
      message: errorMessage,
      error: error,
      validationErrors,
      requestId,
    });
  },
);

// Session management utility
const sessionService = {
  // Get session ID (for backward compatibility)
  getSessionId: () => localStorage.getItem("sessionId") || getJWTToken(),

  // Set session ID (stores both sessionId and token)
  setSessionId: (sessionId) => {
    localStorage.setItem("sessionId", sessionId);
    // Also store as ssToken if it's a JWT token
    if (sessionId && sessionId.length > 20) {
      localStorage.setItem("ssToken", sessionId);
    }
  },

  // Clear all session data
  clearSessionId: () => {
    localStorage.removeItem("sessionId");
    localStorage.removeItem("ssToken");
    localStorage.removeItem("token");
    localStorage.removeItem("ssUser");
  },

  // Check if user is logged in
  isLoggedIn: () => {
    const token = getJWTToken();
    const sessionId = localStorage.getItem("sessionId");
    return !!(token || sessionId);
  },

  // Get JWT token specifically
  getJWTToken: () => getJWTToken(),
};

// ========================================================================
// 🔐 AUTHENTICATION ENDPOINTS
// ========================================================================

/**
 * Login user and create session
 *
 * POST /api/login/
 *
 * Request Payload:
 * {
 *   "username": "user@example.com",
 *   "password": "encrypted_password_from_cryptojs"
 * }
 *
 * Response:
 * {
 *   "status": true,
 *   "message": "Login successful",
 *   "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
 * }
 *
 * @param {string} username - Username/email
 * @param {string} password - Encrypted password (via CryptoJS)
 * @returns {Promise} API response with token and user data
 */
export const loginUser = async (username, password) => {
  try {
    // Login using /api/ticket/login endpoint
    const response = await apiClient.post(`/api/login/`, {
      username: username,
      password: password,
    });

    const data = response.data;

    // Debug: Log the response structure

    // Handle various error response formats - even if status is 200
    const isErrorResponse =
      data?.detail?.status === "false" ||
      data?.detail?.status === false ||
      data?.status === false ||
      data?.status === "false" ||
      data?.error ||
      data?.detail?.error ||
      data?.detail?.status ||
      data?.detail?.message;

    if (isErrorResponse) {
      // Extract error message from various possible locations
      const errorMessage =
        data?.detail?.message ||
        data?.detail?.error_type ||
        data?.detail?.error ||
        data?.message ||
        data?.error ||
        "Invalid credentials";

      console.log("Throwing login error:", errorMessage);
      throw {
        success: false,
        error: errorMessage,
        _isCustomError: true,
      };
    }

    // Handle success response format
    if (data?.status === true || data?.status === "true") {
      const token = data.token;

      // Store token
      sessionService.setSessionId(token);
      localStorage.setItem("ssToken", token);

      // Return user data
      return {
        success: true,
        data: {
          token: token,
          user: {
            id: 1,
            email: username,
            name: username,
            avatar:
              "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(username.split("@")[0]) +
              "&background=0066cc&color=fff",
          },
        },
        message: data.message || "Login successful",
      };
    }

    // Extract token from response - handle multiple possible formats
    let token;

    // Check different possible token locations
    if (data.token) {
    token = data.token;
} else {
    throw new Error(data.message || "Login failed");
}

    // Store token
    sessionService.setSessionId(token);

    // Return user data
    const responseData = {
      success: true,
      data: {
        token: token,
        session_id: token, // Always include session_id
        user: {
          id: 1,
          email: username,
          name: username,
          avatar:
            "https://ui-avatars.com/api/?name=" +
            encodeURIComponent(username.split("@")[0]) +
            "&background=0066cc&color=fff",
        },
      },
      message: data.message || "Login successful",
    };

    return responseData;
  } catch (error) {
    // Handle custom error format from interceptor
    if (error._hasCustomError) {
      throw {
        success: false,
        error: error._customError,
        _isCustomError: true,
      };
    }

    // Check for 401 status specifically to return the actual error message
    if (error.response && error.response.status === 401) {
      const data = error.response.data;
      if (data?.detail?.message) {
        throw {
          success: false,
          error: data.detail.message,
          _isCustomError: true,
        };
      }
    }

    // Normalize all errors to one format
    const message =
      error?.error ||
      error?.response?.data?.detail?.message ||
      error?.response?.data?.message ||
      error?.message ||
      "Login failed";
    throw {
      success: false,
      error: message,
    };
  }
};

/**
 * Logout user and invalidate session
 *
 * POST /api/login/logout?jwt_token=<token>
 *
 * Query Parameters:
 * - jwt_token (required): JWT token from login
 *
 * Response:
 * {
 *   "status": true,
 *   "message": "Logout successful"
 * }
 *
 * @returns {Promise} API response
 */
export const logoutUser = async () => {
  try {
    const token = getJWTToken();

    if (!token) {
      throw {
        success: false,
        error: "No active session found",
        _isCustomError: true,
      };
    }

    const response = await apiClient.post(`/api/login/logout`, null, {
      params: { jwt_token: token },
    });

    // Clear local session
    sessionService.clearSessionId();

    return {
      success: true,
      message: response.data?.message || "Logout successful",
    };
  } catch (error) {
    // Clear local session even if API call fails
    sessionService.clearSessionId();

    throw {
      success: false,
      error: error.message || "Logout failed",
      message: error.message || "Logout failed",
    };
  }
};

/**
 * Validate current JWT token and get user info
 *
 * GET /api/login/validate
 *
 * Headers:
 * Authorization: Bearer <jwt_token>
 *
 * Response (Valid Token):
 * {
 *   "status": true,
 *   "valid": true,
 *   "user_login": "user@example.com"
 * }
 *
 * Response (Invalid Token):
 * {
 *   "status": true,
 *   "valid": false,
 *   "user_login": null
 * }
 *
 * @returns {Promise} API response with validation status
 */
export const validateSession = async () => {
  try {
    const token = getJWTToken();

    if (!token) {
      return {
        success: false,
        valid: false,
        error: "No token found",
      };
    }

    const response = await apiClient.get(`/api/login/validate`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      success: true,
      valid: response.data?.valid === true,
      user_login: response.data?.user_login,
      message: response.data?.message || "Session validated",
    };
  } catch (error) {
    // Token is invalid or expired
    if (error.response?.status === 401) {
      // Clear invalid token
      sessionService.clearSessionId();
      return {
        success: false,
        valid: false,
        error: "Token expired or invalid",
      };
    }

    throw {
      success: false,
      error: error.message || "Session validation failed",
    };
  }
};

// ========================================================================
// 🎫 TICKET ENDPOINTS
// ========================================================================

/**
 * Create a new ticket
 * @param {string} username - Username
 * @param {string} encryptedPassword - Encrypted password
 * @param {Object} ticketData - Ticket data
 * @returns {Promise} API response
 */
export const createTicket = async (username, encryptedPassword, ticketData,sessionId) => {
  try {
    const payload = {
      Ticket: ticketData.Ticket,
      Article: ticketData.Article,
      Attachment: ticketData.Attachment || [],
    };

    const response = await apiClient.post(`/api/TicketCreate`, payload);

    return {
      success: true,
      data: {
        id: response.data.data?.id || Date.now(),
        ticketId: response.data.data?.id || Date.now(),
        caseNumber: response.data.data?.id || Date.now(),
        title: payload.Ticket.Title,
        ticketType: payload.Ticket.Type,
        status: payload.Ticket.State,
        severity: payload.Ticket.Priority,
        createdBy: payload.Ticket.CustomerUser,
        createdOn: new Date().toISOString(),
        description: payload.Article.Body,
        resolution: "",
        Attachment: payload.Attachment,
      },
      message: "Ticket created successfully",
    };
  } catch (error) {
    if (error.response?.status === 401) {
      throw {
        success: false,
        error: "Invalid credentials",
      };
    }

    throw error;
  }
};

/**
 * Create a new ticket using JWT token (for Self Service Portal)
 *
 * POST /api/TicketCreate?jwt_token=<token>
 *
 * Request Schema:
 * {
 *   "Ticket": {
 *     "Type": "Incident",
 *     "Title": "string",
 *     "Queue": "FrothdeskSSP",
 *     "State": "New",
 *     "Priority": "S3"
 *     // CustomerUser is auto-populated from JWT username
 *   },
 *   "Article": {
 *     "Subject": "string",
 *     "Body": "string",
 *     "ContentType": "text/plain; charset=utf-8"
 *   },
 *   "Attachment": [...]
 * }
 *
 * @param {Object} ticketData - Ticket data with Ticket, Article, and Attachment
 * @param {string} sessionId - Session ID (JWT token)
 * @returns {Promise} API response
 */
export const createSelfServiceTicket = async (ticketData, sessionId) => {
  try {
    const payload = {
      Ticket: ticketData.Ticket,
      Article: ticketData.Article,
      Attachment: ticketData.Attachment || [],
    };

    // JWT token passed as query parameter
    const response = await apiClient.post(`/api/TicketCreate`, payload, {
      params: { jwt_token: sessionId },
    });

    const responseData = response.data;

    // Transform response to expected format
    return {
      success: true,
      data: responseData.data || responseData,
      message: responseData.message || "Ticket created successfully",
    };
  } catch (error) {
    console.error("Error in createSelfServiceTicket:", error);
    // Return properly formatted error
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.detail?.message ||
      error.message ||
      error.error ||
      "Failed to create ticket";

    throw {
      success: false,
      error: errorMessage,
      message: errorMessage,
    };
  }
};

/**
 * Get ticket by ID
 * @param {string} username - Username
 * @param {string} encryptedPassword - Encrypted password
 * @param {string} ticketId - Ticket ID
 * @returns {Promise} API response
 */
export const getTicket = async (username, encryptedPassword, ticketId) => {
  try {
    const sessionId = sessionService.getSessionId();

    const response = await apiClient.get(`/api/TicketGet/${ticketId}`, {
      params: {
        jwt_token: sessionId
        // username: username,
        // password: encryptedPassword,
      },
    });

    const ticketData = response.data;

    // Actual ticket object
    const ticket = ticketData.Ticket?.[0] || {};

    return {
      success: true,
      data: {
        id: ticket.TicketID || ticketId,
        ticketId: ticket.TicketID || ticketId,
        caseNumber: ticket.TicketNumber || "-",
        title: ticket.Title || "-",
        ticketType: ticket.Type || "-",
        status: ticket.State || "-",
        severity: ticket.Priority || "-",
        createdBy: ticket.Owner || "-",
        createdOn: ticket.Created || new Date().toISOString(),
        resolution: ticket.Owner || "",
        assignedTo: cleanAssignedTo(ticket.Owner || "-"),
        attachments:
          ticket.Article?.flatMap((article) => article.Attachment) || [],
        comments: ticket.Article || [],
        SLA: ticket.SLA || "-",
      },
      message: "Ticket retrieved successfully",
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get ticket details by ID using session_id, username, and password
 * @param {string} ticketId - Ticket ID
 * @param {string} sessionId - Session ID
 * @returns {Promise} API response
 */
export const getTicketById = async (ticketId, sessionId) => {
  try {
    // Get user credentials from localStorage
    const storedUser = localStorage.getItem("ssUser");

    if (!storedUser) {
      throw {
        success: false,
        error: "User not found. Please log in again.",
        _isCustomError: true,
      };
    }

    const userData = JSON.parse(storedUser);
    const username = userData.username;
    const password = userData.encryptedPassword;

    if (!username) {
      throw {
        success: false,
        error: "Invalid user credentials. Please log in again.",
        _isCustomError: true,
      };
    }

    const response = await apiClient.get(`/api/TicketGet/${ticketId}`, {
      params: {
        jwt_token: sessionId,
        // username: username,
        // password: password,
      },
    });

    const responseData = response.data;

    // Handle different response formats
    const ticket = responseData.Ticket?.[0] || responseData.ticket || {};

    // Ensure ticket is an object and has Article array
    const articles = Array.isArray(ticket.Article) ? ticket.Article : [];

    // Map articles as notes/comments
    const notes = articles.map((article) => {
      // Generate a unique ID - prefer ArticleID, fallback to crypto.randomUUID() or timestamp
      const uniqueId = article.ArticleID || (() => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        return `note-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      })();

      return {
        id: uniqueId,
        date_entered: article.CreateTime || "-",
        subject: article.Subject || "-",
        Body: article.Body || "-",
        Attachment: article.Attachment || [],
      };
    });

    // Map documents/attachments
    const documents = articles.flatMap((article) =>
  Array.isArray(article.Attachment)
    ? article.Attachment.map((att) => ({
        Filename: att.Filename || "Unnamed file",
        Content: att.Content,
        ContentType: att.ContentType,
        FileID: att.FileID,
      }))
    : []
);

    return {
      success: true,
      data: {
        id: ticket.TicketID || ticketId,
        ticketId: ticket.TicketID || ticketId,
        caseNumber: ticket.TicketNumber || "-",
        title: ticket.Title || "-",
        ticketType: ticket.Type || "-",
        status: ticket.State || "-",
        severity: ticket.Priority || "-",
        created_by: ticket.CustomerUserID || ticket.Owner || "-",
        createdDate: ticket.Created || "-",
        assignedTo: cleanAssignedTo(ticket.Owner || "-"),
        resolution_target_time: ticket.SLA || "-",
        ticketDescription: articles[0]?.Body || "-",
        resolution:
          articles.find((a) =>
            a.Subject?.toLowerCase().includes("resolved")
          )?.Body || "-",
        documents,
        notes,
      },
      message: "Ticket retrieved successfully",
    };
  } catch (error) {
    console.error("Error in getTicketById:", error);
    // Re-throw with proper error format
    throw {
      success: false,
      error: error.message || "Failed to fetch ticket details",
      message: error.message || "Failed to fetch ticket details",
    };
  }
};

/**
 * Get all tickets for the user
 * @returns {Promise} API response
 */
export const getAllTickets = async () => {
  try {
    const sessionId = sessionService.getSessionId();

    if (!sessionId) {
      throw {
        success: false,
        error: "No active session. Please log in again.",
        _isCustomError: true,
      };
    }

    // Get user credentials from localStorage
    const storedUser = localStorage.getItem("ssUser");

    if (!storedUser) {
      throw {
        success: false,
        error: "User not found. Please log in again.",
        _isCustomError: true,
      };
    }

    const userData = JSON.parse(storedUser);
    const username = userData.username;
    const password = userData.encryptedPassword;

    if (!username) {
      throw {
        success: false,
        error: "Invalid user credentials. Please log in again.",
        _isCustomError: true,
      };
    }


    const response = await apiClient.get("/api/TicketSearch", {
      params: {
        
        CustomerUserLogin: username,
        jwt_token: sessionId,
        // password: password,
      },
    });

    const responseData = response.data;

    // Ensure tickets is an array
    const ticketsArray = Array.isArray(responseData.tickets)
      ? responseData.tickets
      : Array.isArray(responseData.data)
      ? responseData.data
      : [];

    // Transform API response to match expected format
    const tickets = ticketsArray.map((ticket) => {
      // Ensure Article is an array
      const articles = Array.isArray(ticket.Article) ? ticket.Article : [];

      return {
        id: ticket.TicketID || ticket.id,
        ticketId: ticket.TicketID || ticket.id,
        caseNumber: ticket.TicketNumber || ticket.caseNumber || "-",
        assignedTo: cleanAssignedTo(ticket.Owner || "-"),
        title: ticket.Title || ticket.title || "-",
        ticketType: ticket.Type || ticket.ticketType || "-",
        status: ticket.State || ticket.status || "-",
        severity: ticket.Priority || ticket.severity || "-",

        createdBy: ticket.CustomerUserID || ticket.createdBy || "-",
        createdOn: ticket.Created || ticket.createdOn || "-",

        description: articles[0]?.Body || "-",
        resolution:
          articles.find((a) =>
            a.Subject?.toLowerCase().includes("resolved")
          )?.Body || "-",

        attachments: [],

        comments: articles,
      };
    });

    return {
      success: true,
      data: {
        tickets,
      },
      message: "Tickets retrieved successfully",
    };
  } catch (error) {
    console.error("Error in getAllTickets:", error);
    // Handle authentication errors specifically
    if (error.response && error.response.status === 401) {
      throw {
        success: false,
        error: "Authentication failed. Please log in again.",
        _isCustomError: true,
      };
    }
    // For other errors, use the interceptor's format
    throw {
      success: false,
      error: error.message || error.error || "Failed to fetch tickets",
    };
  }
};

/**
 * Update ticket
 *
 * PUT /api/TicketUpdate/{ticket_id}?jwt_token=<token>
 *
 * Request Schema:
 * {
 *   "state": "string",
 *   "comment": "string",
 *   "Article": {
 *     "Subject": "string",
 *     "Body": "string",
 *     "ContentType": "text/plain; charset=utf-8"
 *   },
 *   "Attachment": [...]
 * }
 *
 * @param {string} ticketId - Ticket ID
 * @param {Object} data - Update data with state, comment, Article, Attachment
 * @param {string} sessionId - Session ID (JWT token)
 * @returns {Promise} API response
 */
export const updateTicket = async (ticketId, data, sessionId) => {
  try {
    // Prepare payload according to new backend requirements
    const updateData = {
      state: data.state,
      comment: data.comment,
      Article: data.Article,
      Attachment: data.Attachment || [],
    };

    // JWT token passed as query parameter
    const response = await apiClient.put(
      `/api/TicketUpdate/${ticketId}`,
      updateData,
      {
        params: { jwt_token: sessionId },
      }
    );

    return {
      success: true,
      data: {
        id: ticketId,
        ticketId: ticketId,
        caseNumber: ticketId,
        title: data.Article?.Subject || "Ticket",
        status: data.state || "Updated",
        updatedOn: new Date().toISOString(),
        message: "Ticket updated successfully",
      },
      message: "Ticket updated successfully",
    };
  } catch (error) {
    // Error is already formatted by the interceptor
    throw error;
  }
};

// ========================================================================
// 📞 CONTACT US ENDPOINTS
// ========================================================================

/**
 * Create a new lead in Zoho CRM (contact form submission)
 *
 * POST /api/create_lead
 *
 * Request Payload:
 * {
 *   "last_name": "John Doe",
 *   "phone": "+1234567890",
 *   "email": "user@example.com",
 *   "company": "Company Inc",
 *   "designation": "Manager",
 *   "message": "I'm interested in your services"
 * }
 *
 * Response:
 * {
 *   "status": "success",
 *   "message": "Lead created successfully"
 * }
 *
 * Validation Rules:
 * - last_name: 1-100 chars, letters and spaces only
 * - phone: 10-17 digits, international format
 * - email: Valid email format
 * - company: 1-200 chars, letters, numbers, spaces, hyphens, apostrophes, dots, and &
 * - designation: 1-100 chars, letters, numbers, spaces, hyphens, apostrophes, dots, &, and /
 * - message: 1-2000 chars
 *
 * Rate Limiting: 10 requests per 60 seconds per IP
 *
 * @param {Object} leadData - Lead information
 * @param {string} leadData.last_name - Full name (required)
 * @param {string} leadData.phone - Phone number (required)
 * @param {string} leadData.email - Email address (required)
 * @param {string} leadData.company - Company name (optional)
 * @param {string} leadData.designation - Job title (optional)
 * @param {string} leadData.message - Message (optional)
 * @returns {Promise} API response
 */
export const createLead = async (leadData) => {
  try {
    const payload = {
      last_name: leadData.last_name || "",
      phone: leadData.phone || "",
      email: leadData.email || "",
      company: leadData.company || "",
      designation: leadData.designation || "",
      message: leadData.message || "",
    };

    const response = await apiClient.post(`/api/create_lead`, payload);

    return {
      success: true,
      message: response.data?.message || "Lead created successfully",
      data: response.data,
    };
  } catch (error) {
    // Handle rate limiting specifically
    if (error.response?.status === 429) {
      throw {
        success: false,
        error: "Too many requests. Please wait before submitting again.",
        _isCustomError: true,
      };
    }

    // Handle validation errors
    if (error.response?.status === 400) {
      const validationErrors = error.response.data?.detail || [];
      throw {
        success: false,
        error: "Validation failed",
        validationErrors,
        _isCustomError: true,
      };
    }

    throw {
      success: false,
      error: error.message || "Failed to create lead",
    };
  }
};

// ========================================================================
// 🔍 HEALTH & UTILITY ENDPOINTS
// ========================================================================

/**
 * Health check endpoint for ticket service
 *
 * GET /api/health
 *
 * Response:
 * {
 *   "status": "ok",
 *   "service": "ticket-service"
 * }
 *
 * @returns {Promise} API response with service health status
 */
export const healthCheck = async () => {
  try {
    const response = await apiClient.get(`/api/health`);

    return {
      success: true,
      status: response.data?.status || "ok",
      service: response.data?.service || "ticket-service",
    };
  } catch (error) {
    throw {
      success: false,
      error: error.message || "Health check failed",
    };
  }
};

/**
 * Simple API test endpoint
 *
 * GET /api/test-simple
 *
 * Response:
 * {
 *   "message": "Simple API test is working",
 *   "timestamp": "2026-04-27T12:30:00Z"
 * }
 *
 * @returns {Promise} API response with test message and timestamp
 */
export const testSimple = async () => {
  try {
    const response = await apiClient.get(`/api/test-simple`);

    return {
      success: true,
      message: response.data?.message || "API test working",
      timestamp: response.data?.timestamp || new Date().toISOString(),
    };
  } catch (error) {
    throw {
      success: false,
      error: error.message || "API test failed",
    };
  }
};

// Export API client for custom requests if needed
export { apiClient };

// Export session service
export { sessionService };

// Make session service available globally
if (typeof window !== "undefined") {
  window.sessionService = sessionService;
}

export default {
  // Authentication
  loginUser,
  logoutUser,
  validateSession,

  // Tickets
  createTicket,
  createSelfServiceTicket,
  getTicket,
  getTicketById,
  getAllTickets,
  updateTicket,

  // Contact
  createLead,

  // Health & Utility
  healthCheck,
  testSimple,

  // Session
  sessionService,
};
